import React, { useEffect, useState } from 'react';
import i18n from 'i18next';
import PropTypes from 'prop-types';
import { css } from '@emotion/core';
import { system900, system800, system300, system100, system700 } from 'Mui/styles/color';
import Typography from '@material-ui/core/Typography';
import { SHARE_CARD_IMAGE_TYPES } from 'editor/game_mgmShare/utils/constants';
import InfoIcon from '@material-ui/icons/Info';
import IgUseHtmlCodeToDisplay from 'ugc/components/ugcPortfolio/IgUseHtmlCodeToDisplay';

const calculateImageDisplaySize = (width, height) => {
    const MAX_WIDTH = 270;
    const MAX_HEIGHT = 340;
    if (width === height) {
        return { width: MAX_WIDTH, height: MAX_WIDTH };
    }
    //橫向圖片
    if (width > height) {
        //以最大寬度為基準，等比例縮放高度
        const finalWidth = MAX_WIDTH;
        return {
            width: finalWidth,
            height: (height / width) * finalWidth
        };
    }
    //直向圖片
    //先試以最大高度為基準
    let finalHeight = MAX_HEIGHT;
    let finalWidth = (width / height) * finalHeight;
    // 如果計算出的寬度超過限制，改以最大寬度為基準
    if (finalWidth > MAX_WIDTH) {
        finalWidth = MAX_WIDTH;
        finalHeight = (height / width) * finalWidth;
    }

    return { width: finalWidth, height: finalHeight };
};

// 從 YouTube URL 中提取video ID
const getYoutubeVideoId = url => {
    if (!url) return null;
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return match && match[2].length === 11 ? match[2] : null;
};

const Hero = ({
    type,
    imageUrl,
    variant,
    isEditAndCustomResult,
    isEditAndWorkPage,
    mediaData,
    isIgGame
}) => {
    const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

    useEffect(() => {
        if (!imageUrl) return;

        const img = new Image();
        img.onload = () => {
            const newDimensions = calculateImageDisplaySize(img.width, img.height);
            setDimensions(newDimensions);
        };
        img.src = imageUrl;
    }, [imageUrl]);

    const renderContent = () => {
        // Portfolio 內容顯示
        if (mediaData?.length > 0) {
            const { result, type: mediaType, subtype } = mediaData[0];

            if (isIgGame) {
                return (
                    <div css={heroMediaStyle}>
                        <IgUseHtmlCodeToDisplay mediaData={mediaData} />
                    </div>
                );
            }

            if (mediaType === 'video' && subtype === 'youtube') {
                const videoId = getYoutubeVideoId(result);
                if (videoId) {
                    return (
                        <div
                            css={heroStyle(
                                type,
                                `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`,
                                variant
                            )}
                        >
                            <div css={imageWrapperStyle}>
                                <span></span>
                            </div>
                        </div>
                    );
                }
            }

            return (
                <div css={heroStyle(type, result, variant)}>
                    <div css={imageWrapperStyle}>
                        <span></span>
                    </div>
                </div>
            );
        }
        // 客製化結果和作品集editor樣式
        if (isEditAndCustomResult || isEditAndWorkPage) {
            return (
                <div css={customImageWrapperStyle}>
                    <InfoIcon css={iconStyle} />
                    {i18n.t(
                        isEditAndCustomResult
                            ? 'label.result_image_type.auto_show'
                            : 'label.result_image_type.auto_show_work_page'
                    )}
                </div>
            );
        }

        // 自訂圖片顯示
        return imageUrl ? (
            <div css={heroContainerStyle(type, variant)}>
                <div css={imageContainerStyle(dimensions)}>
                    <img
                        src={imageUrl}
                        css={imageStyle(dimensions, imageUrl)}
                        alt="referral-share-card"
                    />
                </div>
            </div>
        ) : (
            <div css={customImageWrapperStyle} />
        );
    };

    return renderContent();
};

const Body = ({ type, isShowImage, children, variant }) => {
    return <div css={bodyStyle(type, isShowImage, variant)}>{children}</div>;
};

const Title = ({ children }) => {
    return <div css={titleStyle}>{children}</div>;
};

const Description = ({ children }) => {
    return <div css={descriptionStyle}>{children}</div>;
};

const Referral = ({ code }) => {
    return <div css={referralStyle}>{`${i18n.t('label.referral_code')}: ${code}`}</div>;
};

const ActionButton = ({ buttonConfig }) => {
    const dynamicProps = buttonConfig?.uri ? { href: buttonConfig.uri, alt: buttonConfig.uri } : {};
    return (
        <div css={actionStyle}>
            <a {...dynamicProps} css={buttonStyle(buttonConfig)}>
                <Typography
                    variant="subtitle2"
                    css={[
                        textStyle,
                        css`
                            color: ${buttonConfig.textColor};
                        `
                    ]}
                >
                    {buttonConfig.label}
                </Typography>
            </a>
        </div>
    );
};

const ReferralShareCard = ({
    variant = 'contained',
    type = 'default',
    title,
    description,
    imageUrl,
    isShowImage,
    buttonConfig = {},
    referralCode,
    isShowReferralCode,
    resultImageType,
    isEditor,
    mediaData,
    isIgGame
}) => {
    return (
        <div css={containerStyle(type)}>
            {isShowImage && (
                <Hero
                    type={type}
                    imageUrl={imageUrl}
                    variant={variant}
                    isEditAndCustomResult={
                        resultImageType === SHARE_CARD_IMAGE_TYPES.GAME_RESULT && isEditor
                    }
                    isEditAndWorkPage={
                        resultImageType === SHARE_CARD_IMAGE_TYPES.WORK_PAGE && isEditor
                    }
                    mediaData={mediaData}
                    isIgGame={isIgGame}
                />
            )}
            <Body type={type} isShowImage={isShowImage} variant={variant}>
                <Title>{title}</Title>
                <Description>{description}</Description>
                {isShowReferralCode && type === 'line' && <Referral code={referralCode} />}
                {buttonConfig?.enableDisplay && type === 'line' && (
                    <ActionButton buttonConfig={buttonConfig} />
                )}
            </Body>
        </div>
    );
};

const containerStyle = type => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    color: ${system900.color};
    background: ${type === 'default' ? 'transparent' : '#fff'};
    width: 100%;
    border: 1px solid ${system300.color};
    border-radius: 16px;
`;

const heroStyle = (type, imageUrl, variant) => css`
    width: 100%;
    border-top-right-radius: inherit;
    border-top-left-radius: inherit;
    overflow: hidden;
    border-bottom-width: 0px;


    ${
        variant === 'outlined' &&
        `
    border: 1px solid ${system300.color};
`
    }
    ${
        type === 'line' &&
        `
        border-bottom-width: 0px;
    `
    }
    & span {
        display: block;
        position: absolute;
        left: 0;
        right: 0;
        top: 0;
        bottom: 0;
        border-bottom-right-radius: inherit;
        border-bottom-left-radius: inherit;
       
        ${
            type === 'default' &&
            `
            border-bottom-right-radius: inherit;
            border-bottom-left-radius: inherit;
        `
        }
        background-image: url(${imageUrl});
        background-size: cover;
        background-repeat: no-repeat;
        background-position: center center;
        overflow: hidden;
`;

const imageWrapperStyle = css`
    display: block;
    position: relative;
    width: 100%;
    aspect-ratio: 1.52 / 1;
`;

const bodyStyle = (type, isShowImage, variant) => css`
    display: flex;
    flex-direction: column;
    padding-inline: 16px;
    padding-block: 12px;
    background: #fff;
    border-bottom-right-radius: inherit;
    border-bottom-left-radius: inherit;
    margin-top: 0;
    ${
        variant === 'outlined' &&
        `
        border: 1px solid ${system300.color};
    `
    }


    ${
        !isShowImage &&
        `
        border-top-right-radius: inherit;
        border-top-left-radius: inherit;
    `
    }
    ${
        type === 'line' &&
        isShowImage &&
        `
        border-top-width: 0px;
    `
    }
    overflow: hidden;
    gap: 4px;
    width: 100%;
`;

const titleStyle = css`
    align-self: stretch;
    color: ${system900.color};
    font-size: 20px;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
    letter-spacing: 0.15px;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const descriptionStyle = css`
    align-self: stretch;
    color: ${system800.color};
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
    letter-spacing: 0.1px;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const referralStyle = css`
    width: 100%;
    padding-block: 8px;
    margin-block: 4px;
    text-align: center;
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
    letter-spacing: 1.25px;
    text-transform: capitalize;
`;

const actionStyle = css`
    flex: none;
    flex-basis: auto;
    overflow: hidden;
    position: relative;
`;

const buttonStyle = buttonConfig => css`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 36px;
    padding: 0 16px;
    color: ${buttonConfig.textColor};
    background-color: ${buttonConfig.bgColor};
    border-radius: 8px;

    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
    letter-spacing: 1.25px;
    text-transform: capitalize;
    text-decoration: none;
`;

const textStyle = css`
    overflow: hidden;
    text-overflow: ellipsis;
    box-sizing: border-box;
    white-space: nowrap;
`;

export default ReferralShareCard;

ReferralShareCard.propTypes = {
    type: PropTypes.oneOf(['default', 'line']),
    description: PropTypes.string,
    imageUrl: PropTypes.string,
    buttonConfig: PropTypes.shape({
        enableDisplay: PropTypes.bool,
        label: PropTypes.string,
        uri: PropTypes.string,
        textColor: PropTypes.string,
        bgColor: PropTypes.string
    }),
    referralCode: PropTypes.string,
    isShowReferralCode: PropTypes.bool
};

const iconStyle = css`
    margin-right: 8px;
`;

const customImageWrapperStyle = css`
    border-top-right-radius: inherit;
    border-top-left-radius: inherit;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 136px;
    background: ${system100.color};
    color: ${system700.color};
    font-size: 14px;
    font-weight: 500;
`;

const heroMediaStyle = css`
    width: 100%;
    border-top-right-radius: inherit;
    border-top-left-radius: inherit;
    overflow-y: auto;
    display: flex;
    justify-content: center;
    align-items: center;
    height: 200px;
`;

const heroContainerStyle = (type, variant) => css`
    width: 100%;
    border-top-right-radius: inherit;
    border-top-left-radius: inherit;
    overflow: hidden;
    border-bottom-width: 0px;
    display: flex;
    justify-content: center;

    ${variant === 'outlined' &&
    `
        border: 1px solid ${system300.color};
    `}
    ${type === 'line' &&
    `
        border-bottom-width: 0px;
    `}
`;

const imageContainerStyle = dimensions => css`
    position: relative;
    width: ${dimensions.width}px;
    height: ${dimensions.height}px;
`;

const imageStyle = (dimensions, imageUrl) => css`
    display: block;
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    width: ${dimensions.width}px;
    height: ${dimensions.height}px;
    background-image: url(${imageUrl});
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
    border-bottom-right-radius: inherit;
    border-bottom-left-radius: inherit;
`;
